import axios from "axios";
import constants from "../../../constants";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { selectCurrentCustomerNumber } from "../../../redux/customer";
import { useState, useMemo, ChangeEvent } from "react";
import { Box, createTheme, Paper, Link, useTheme, ThemeProvider, TextField, IconButton } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { SDSItem } from "./types";
import PageHeader from "../../../components/PageHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import debounce from "../../../helpers/debounce";
import styled from "@emotion/styled";
import { addError, addWarning } from "../../../redux/alerts";
import { getSdsLink } from "../meds-devices/common/utils/helpers";

const SDS = () => {
  const customerNumber = useAppSelector(selectCurrentCustomerNumber);
  const dispatch = useAppDispatch();
  const [sdsItems, setSdsItems] = useState<SDSItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const theme = useTheme();

  const tableTheme = createTheme({
    typography: {
      fontFamily: '"Open Sans", sans-serif',
      fontSize: 13
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: "bold"
          }
        }
      }
    }
  })

  const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}:nth-of-type(odd)`]: {
      backgroundColor: '#f9f9f9',
    }
  }));

  const getSDSItems = async () => {
    try {
      const response = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/items/sds`);
      setSdsItems(response.data);
      setIsLoading(false);
    } catch (error) {
      dispatch(addError(`There was an issue with your request, please try again later.`));
      console.error("Error fetching SDS items:", error);
    }
  }

  useMemo(() => {
    if (customerNumber) {
      getSDSItems();
    }
  }, [customerNumber]);

  const filteredItems = useMemo(() => {
    return search === '' ? sdsItems : sdsItems.filter(item => {
      const cleanedSearch = search.trim().toLowerCase();
      const name = item.name?.trim().toLowerCase();
      const itemNumber = item.item_number?.trim();
      const manufacturerName = item.manufacturer_name?.trim().toLowerCase();
      return name?.includes(cleanedSearch) || itemNumber?.includes(cleanedSearch) || manufacturerName?.includes(cleanedSearch);
    })
  }, [search, sdsItems])

  const getLink = async (itemNumber: string) => {
    try {
      const link = await getSdsLink(customerNumber, itemNumber)

      if (link) window.open(link, '_blank')
      else {
        dispatch(addWarning('We are sorry, the SDS you have requested is currently unavailable. Please check back in the next week. If you have questions, please email us at ontraq@healthfirst.com'))
      }
    } catch (error: any) {
      console.error(error);
      if (error?.response?.status === 404) {
        dispatch(addWarning('We are sorry, the SDS you have requested is currently unavailable. Please check back in the next week. If you have questions, please email us at ontraq@healthfirst.com'))
      } else {
        dispatch(addError(`There was an issue with your request, please try again later.`));
      }
    }
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const searchValue = event.target.value
    setSearch(searchValue)
  }

  const debouncedHandleSearch = debounce(handleSearch, 50)

  return (
    <>
      <PageHeader iconName={['far', 'folder-open']} headerName={'Download SDSs (Safety Data Sheets)'} size={'26px'} />
      <Box sx={{ color: theme.palette.color50555b.main, padding: '16px' }}>
        <Box className="table-subheader flex flex-space-between" sx={{ padding: '16px 0px' }}>
          <Box>
            <TextField
              sx={{ minWidth: 200 }}
              size="small"
              label="Search"
              onChange={debouncedHandleSearch}
              InputProps={
                {
                  endAdornment: (
                    <IconButton>
                      <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
                    </IconButton>
                  )
                }
              }
            />
          </Box>
        </Box>
        <Box>
          <Paper>
            <ThemeProvider theme={tableTheme}>
              <StyledDataGrid
                getRowId={(row) => row.item_number}
                sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
                rows={filteredItems}
                columns={[
                  {
                    flex: 1,
                    minWidth: 800,
                    field: 'name',
                    headerName: 'Download PDF',
                    sortable: true,
                    renderCell: (params) => (
                      <Link sx={{ color: theme.palette.linkColor.main, cursor: 'pointer' }} onClick={() => getLink(params.row.item_number)}>
                        <FontAwesomeIcon icon={['far', 'file-pdf']} />
                        <span style={{ marginLeft: 15 }}>{params.row.name}</span>
                      </Link>
                    )
                  },
                  {
                    flex: 1,
                    field: 'manufacturer_name',
                    headerName: 'Manufacturer PDF',
                    sortable: true,
                    renderCell: (params) => (
                      <span>{params.row.manufacturer_name}</span>
                    )
                  },
                  {
                    flex: 1,
                    field: 'item_number',
                    headerName: 'Item Number',
                    sortable: true,
                    renderCell: (params) => (
                      <span>{params.row.item_number}</span>
                    )
                  }
                ]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 25,
                    },
                  },
                  sorting: {
                    sortModel: [
                      {
                        field: 'name',
                        sort: 'desc'
                      }
                    ]
                  }
                }}
                pageSizeOptions={[5, 10, 20, 25, 50, 100]}
                loading={isLoading}
                autoHeight
                density="compact"
                slotProps={{
                  loadingOverlay: {
                    variant: 'skeleton',
                    noRowsVariant: 'skeleton',
                  },
                }}
              />
            </ThemeProvider>
          </Paper>
        </Box>
      </Box>
    </>
  );
}

export default SDS;